import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  Linking,
} from 'react-native';
import paymentProviderService from '../services/paymentProviderService';
import MerchantPaymentTermsAgreement from '../components/MerchantPaymentTermsAgreement';
import StripeConnectApprovalSection from '../components/StripeConnectApprovalSection';
import PayPalDirectSetupModal from '../components/PayPalDirectSetupModal';
import { STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL, SHOW_STRIPE_DIRECT_LINK_SETUP, STRIPE_CONNECT_FOR_DELIVERY_ENABLED } from '../constants/paymentProviderFlags';
import { SELLER_PAYPAL_SETUP_HELP, SELLER_PAYPAL_CONFIRM_HELP } from '../constants/paymentHelpText';
import { hasValidMerchantTermsAcceptance } from '../services/merchantTermsService';
import { AuthManager } from '../utils/authManager';

interface BusinessSetupPaymentScreenProps {
  businessId: string;
  businessName: string;
  onComplete: () => void;
  onSkip: () => void;
}

const BusinessSetupPaymentScreen: React.FC<BusinessSetupPaymentScreenProps> = ({
  businessId,
  businessName,
  onComplete,
  onSkip,
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [connectedProviders, setConnectedProviders] = useState<string[]>([]);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [merchantTermsAccepted, setMerchantTermsAccepted] = useState(false);
  const [stripeAccessApproved, setStripeAccessApproved] = useState(false);
  const [showPayPalDirectModal, setShowPayPalDirectModal] = useState(false);

  // Check connection status on mount and handle OAuth callbacks
  useEffect(() => {
    checkConnectionStatus();
    handleOAuthCallback();
    AuthManager.getSavedBusinessOwner().then((owner) => {
      if (hasValidMerchantTermsAcceptance(owner)) {
        setMerchantTermsAccepted(true);
      }
    });
  }, []);

  // Handle OAuth callback from Stripe/PayPal
  const handleOAuthCallback = async () => {
    // This would be called when app returns from OAuth flow
    // For now, we'll check status periodically
    // In production, use deep linking to handle callbacks
  };

  // Check if payment providers are connected
  const checkConnectionStatus = async () => {
    setCheckingStatus(true);
    try {
      const next: string[] = [];
      const paypalStatus = await paymentProviderService.checkPayPalStatus(businessId);
      if (paypalStatus.connected && paypalStatus.ready) {
        next.push('paypal');
      }
      if (STRIPE_CONNECT_FOR_DELIVERY_ENABLED) {
        const stripeStatus = await paymentProviderService.checkStripeStatus(businessId);
        if (stripeStatus.connected && stripeStatus.ready) {
          next.push('stripe');
        }
      }
      setConnectedProviders(next);
    } catch (error) {
      console.log('Error checking connection status:', error);
      // Silently fail - will show as not connected
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleDisconnectProvider = (providerId: 'paypal' | 'stripe') => {
    const label = providerId === 'paypal' ? 'PayPal' : 'Stripe';
    Alert.alert(
      `Disconnect ${label}`,
      `Are you sure you want to disconnect ${label}? You won't be able to receive payments via ${label} until you link again.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Disconnect',
          style: 'destructive',
          onPress: async () => {
            setLoading(`disconnect-${providerId}`);
            try {
              const result =
                providerId === 'paypal'
                  ? await paymentProviderService.disconnectPayPal(businessId)
                  : await paymentProviderService.disconnectStripe(businessId);
              if (result.success) {
                setConnectedProviders((prev) => prev.filter((p) => p !== providerId));
                Alert.alert(
                  'Disconnected',
                  `Your ${label} link has been removed. Tap "Link ${label}" to connect again.`,
                );
              } else {
                Alert.alert('Error', result.error || 'Failed to disconnect. Please try again.');
              }
            } catch {
              Alert.alert('Error', 'Failed to disconnect. Please try again.');
            } finally {
              setLoading(null);
            }
          },
        },
      ],
    );
  };

  const ensureMerchantTerms = (provider: 'stripe' | 'paypal'): boolean => {
    if (merchantTermsAccepted) {
      return true;
    }
    Alert.alert(
      'Merchant terms required',
      'Please read the Merchant Payment Terms and check every box before linking Stripe or PayPal.',
    );
    return false;
  };

  const ensureStripeApproved = (): boolean => {
    if (!STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL) {
      return true;
    }
    if (stripeAccessApproved) {
      return true;
    }
    Alert.alert(
      'Approval required',
      'Submit a payment access request and wait for BookAPT approval before connecting Stripe.',
    );
    return false;
  };

  const needsTermsForStripe =
    STRIPE_CONNECT_FOR_DELIVERY_ENABLED &&
    !connectedProviders.includes('stripe');

  const showMerchantTermsSection =
    !connectedProviders.includes('paypal') || needsTermsForStripe;

  const handleConnectStripe = async () => {
    if (!ensureMerchantTerms('stripe')) {
      return;
    }
    if (!STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL && !STRIPE_CONNECT_FOR_DELIVERY_ENABLED) {
      return;
    }
    if (!ensureStripeApproved()) {
      return;
    }
    setLoading('stripe');
    try {
      // Create Stripe Connect account link via API
      const result = await paymentProviderService.createStripeConnectAccount(
        businessId,
        businessName,
        'bookapt://stripe-connect-success',
        'bookapt://stripe-connect-refresh'
      );

      // Handle backend not available
      if (result.error === 'BACKEND_NOT_AVAILABLE') {
        Alert.alert(
          'Backend API Not Available',
          'The payment provider API is not currently available. To set up Stripe:\n\n' +
          '1. Go to https://dashboard.stripe.com\n' +
          '2. Sign up or log in to your Stripe account\n' +
          '3. Complete your business verification\n' +
          '4. Contact support@bookapt.io to complete the connection\n\n' +
          'Or click "Open Stripe Dashboard" to go there now.',
          [
            { text: 'Cancel', style: 'cancel', onPress: () => setLoading(null) },
            { 
              text: 'Open Stripe Dashboard', 
              onPress: async () => {
                const stripeUrl = 'https://dashboard.stripe.com/register';
                const canOpen = await Linking.canOpenURL(stripeUrl);
                if (canOpen) {
                  await Linking.openURL(stripeUrl);
                } else {
                  Alert.alert('Error', 'Cannot open Stripe dashboard. Please visit https://dashboard.stripe.com manually.');
                }
                setLoading(null);
              }
            }
          ]
        );
        return;
      }

      if (result.success && result.connectUrl) {
        // Open Stripe OAuth page
        const canOpen = await Linking.canOpenURL(result.connectUrl);
        if (canOpen) {
          await Linking.openURL(result.connectUrl);
          
          Alert.alert(
            'Redirecting to Stripe',
            'You will be redirected to Stripe to complete the setup. When you\'re done, return to this app.',
            [{ text: 'OK' }]
          );
          
          // Check status periodically (in production, use deep linking)
          const checkInterval = setInterval(async () => {
            await checkConnectionStatus();
            if (connectedProviders.includes('stripe')) {
              clearInterval(checkInterval);
              Alert.alert(
                'Stripe Connected! ✅',
                'Your Stripe account has been connected successfully. You can now accept payments for your services.',
                [{ text: 'Great!' }]
              );
            }
          }, 3000);
          
          // Stop checking after 5 minutes
          setTimeout(() => clearInterval(checkInterval), 300000);
        } else {
          Alert.alert('Error', 'Cannot open Stripe connection page. Please check your internet connection.');
          setLoading(null);
        }
      } else if (result.error === 'MERCHANT_TERMS_REQUIRED') {
        Alert.alert(
          'Merchant terms required',
          'Accept all merchant payment terms before connecting Stripe.',
        );
        setLoading(null);
      } else if (
        result.error === 'STRIPE_APPROVAL_REQUIRED' ||
        String(result.error || '').toLowerCase().includes('approval')
      ) {
        Alert.alert(
          'Approval required',
          'Your payment access request must be approved by BookAPT before connecting Stripe.',
        );
        setLoading(null);
      } else {
        Alert.alert('Error', result.error || 'Failed to connect to Stripe. Please try again.');
        setLoading(null);
      }
    } catch (error: any) {
      console.error('Stripe connection error:', error);
      Alert.alert('Error', error.message || 'Failed to connect to Stripe. Please try again or contact support.');
      setLoading(null);
    }
  };

  const handleConnectPayPal = () => {
    if (!ensureMerchantTerms('paypal')) {
      return;
    }
    setShowPayPalDirectModal(true);
  };

  const handleComplete = () => {
    if (connectedProviders.length === 0) {
      Alert.alert(
        'No Payment Methods',
        'You haven\'t connected any payment methods. You can still use the app for appointments and add payment methods later in settings.',
        [
          { text: 'Continue Anyway', onPress: onComplete },
          { text: 'Go Back', style: 'cancel' }
        ]
      );
    } else {
      onComplete();
    }
  };

  const paymentProviders = [
    ...(SHOW_STRIPE_DIRECT_LINK_SETUP ||
    STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL ||
    STRIPE_CONNECT_FOR_DELIVERY_ENABLED
      ? [
          {
            id: 'stripe',
            name: 'Stripe Connect',
            icon: '💳',
            description: STRIPE_CONNECT_FOR_DELIVERY_ENABLED
              ? 'Required for delivery — split checkout; BookAPT service fee via application fee'
              : 'Customers pay your Stripe Payment Link — BookAPT does not process funds',
            color: '#635bff',
            connected: connectedProviders.includes('stripe'),
            onPress: handleConnectStripe,
          },
        ]
      : []),
    {
      id: 'paypal',
      name: 'PayPal',
      icon: '🔵',
      description: 'Customers pay your PayPal.me — BookAPT does not process funds',
      color: '#0070ba',
      connected: connectedProviders.includes('paypal'),
      onPress: handleConnectPayPal,
    },
  ];

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Set Up Payment Processing</Text>
        <Text style={styles.subtitle}>
          Link PayPal for pickup payments. Link Stripe Connect to offer delivery.
        </Text>
      </View>

      {!connectedProviders.includes('paypal') ? (
        <View style={styles.benefitsContainer}>
          <Text style={styles.benefitsTitle}>PayPal direct payments</Text>
          <Text style={styles.benefitItem}>• Customers pay you on PayPal.me (PayPal or card)</Text>
          <Text style={styles.benefitItem}>• BookAPT does not hold or process appointment funds</Text>
          <Text style={styles.benefitItem}>• You confirm payment in BookAPT after PayPal notifies you</Text>
        </View>
      ) : null}

      {STRIPE_CONNECT_FOR_DELIVERY_ENABLED && !connectedProviders.includes('stripe') ? (
        <View style={styles.benefitsContainer}>
          <Text style={styles.benefitsTitle}>Stripe Connect (delivery)</Text>
          <Text style={styles.benefitItem}>• Required to enable delivery checkout</Text>
          <Text style={styles.benefitItem}>• Customers pay on Stripe; you receive food subtotal</Text>
          <Text style={styles.benefitItem}>• BookAPT service fee ($1.49) goes to BookAPT automatically</Text>
        </View>
      ) : null}

      {!connectedProviders.includes('paypal') ? (
        <View style={styles.sellerHelpBox}>
          <Text style={styles.sellerHelpText}>{SELLER_PAYPAL_SETUP_HELP}</Text>
          <Text style={[styles.sellerHelpText, styles.sellerHelpTextSpaced]}>{SELLER_PAYPAL_CONFIRM_HELP}</Text>
        </View>
      ) : null}

      {showMerchantTermsSection ? (
        <View style={styles.termsSection}>
          <MerchantPaymentTermsAgreement
            businessId={businessId}
            provider="both"
            onAllAcceptedChange={setMerchantTermsAccepted}
          />
          {STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL &&
          !connectedProviders.includes('stripe') ? (
            <StripeConnectApprovalSection
              businessId={businessId}
              merchantTermsAccepted={merchantTermsAccepted}
              onCanConnectChange={setStripeAccessApproved}
            />
          ) : null}
        </View>
      ) : null}

      <View style={styles.providersContainer}>
        {paymentProviders.map((provider) => (
          <View key={provider.id} style={styles.providerCard}>
            <View style={styles.providerHeader}>
              <View style={styles.providerIcon}>
                <Text style={styles.providerIconText}>{provider.icon}</Text>
              </View>
              <View style={styles.providerInfo}>
                <Text style={styles.providerName}>{provider.name}</Text>
                <Text style={styles.providerDescription}>{provider.description}</Text>
              </View>
              {provider.connected && (
                <View style={styles.connectedBadge}>
                  <Text style={styles.connectedText}>✓ Connected</Text>
                </View>
              )}
            </View>

            {provider.connected ? (
              <View style={styles.connectedActions}>
                {provider.id === 'paypal' ? (
                  <TouchableOpacity
                    style={[styles.connectButton, { backgroundColor: provider.color }]}
                    onPress={() => setShowPayPalDirectModal(true)}
                    disabled={loading !== null}
                  >
                    <Text style={styles.connectButtonText}>Update link</Text>
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity
                  style={[
                    styles.disconnectButton,
                    { borderColor: provider.color },
                    loading === `disconnect-${provider.id}` && styles.loadingButton,
                  ]}
                  onPress={() => handleDisconnectProvider(provider.id as 'paypal' | 'stripe')}
                  disabled={loading !== null}
                >
                  {loading === `disconnect-${provider.id}` ? (
                    <ActivityIndicator color={provider.color} />
                  ) : (
                    <Text style={[styles.disconnectButtonText, { color: provider.color }]}>
                      Disconnect
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                style={[
                  styles.connectButton,
                  { backgroundColor: provider.color },
                  loading === provider.id && styles.loadingButton,
                  !merchantTermsAccepted && styles.connectButtonDisabled,
                  STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL &&
                    provider.id === 'stripe' &&
                    !stripeAccessApproved &&
                    styles.connectButtonDisabled,
                ]}
                onPress={() => {
                  provider.onPress();
                }}
                disabled={
                  loading !== null ||
                  !merchantTermsAccepted ||
                  (STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL &&
                    provider.id === 'stripe' &&
                    !stripeAccessApproved)
                }
              >
                {loading === provider.id ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text style={styles.connectButtonText}>
                    {provider.id === 'paypal'
                      ? 'Link PayPal'
                      : provider.id === 'stripe'
                        ? 'Link Stripe'
                        : `Connect ${provider.name}`}
                  </Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        ))}
      </View>

      <View style={styles.pricingContainer}>
        <Text style={styles.pricingTitle}>Pricing</Text>
        <Text style={styles.pricingText}>
          BookAPT uses a subscription model. You pay a monthly fee via App Store subscription.
          No per-transaction fees - you keep 100% of your service revenue!
        </Text>
        <Text style={styles.pricingExample}>
          Example: $100 service → You receive $97.10 (only processing fee)
        </Text>
      </View>

      <View style={styles.buttonsContainer}>
        <TouchableOpacity style={styles.skipButton} onPress={onSkip}>
          <Text style={styles.skipButtonText}>Skip for Now</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.completeButton} onPress={handleComplete}>
          <Text style={styles.completeButtonText}>
            {connectedProviders.length > 0 ? 'Complete Setup' : 'Continue Without Payments'}
          </Text>
        </TouchableOpacity>
      </View>

      <PayPalDirectSetupModal
        visible={showPayPalDirectModal}
        businessId={businessId}
        onClose={() => setShowPayPalDirectModal(false)}
        onSaved={() => checkConnectionStatus()}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    padding: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#6c757d',
    lineHeight: 22,
  },
  benefitsContainer: {
    backgroundColor: 'white',
    padding: 20,
    margin: 20,
    borderRadius: 12,
  },
  benefitsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 12,
  },
  benefitItem: {
    fontSize: 16,
    color: '#495057',
    marginBottom: 8,
    lineHeight: 22,
  },
  sellerHelpBox: {
    backgroundColor: '#e8f4fc',
    marginHorizontal: 20,
    marginBottom: 12,
    padding: 14,
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#0070ba',
  },
  sellerHelpText: {
    fontSize: 13,
    color: '#334155',
    lineHeight: 19,
  },
  sellerHelpTextSpaced: {
    marginTop: 10,
  },
  termsSection: {
    paddingHorizontal: 20,
    paddingTop: 8,
  },
  providersContainer: {
    padding: 20,
  },
  connectButtonDisabled: {
    opacity: 0.45,
  },
  providerCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  providerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  providerIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  providerIconText: {
    fontSize: 24,
  },
  providerInfo: {
    flex: 1,
  },
  providerName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  providerDescription: {
    fontSize: 14,
    color: '#6c757d',
  },
  connectedBadge: {
    backgroundColor: '#d4edda',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  connectedText: {
    color: '#155724',
    fontSize: 12,
    fontWeight: '600',
  },
  connectButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  connectedActions: {
    gap: 10,
  },
  loadingButton: {
    opacity: 0.7,
  },
  disconnectButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  disconnectButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  connectButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  pricingContainer: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    margin: 20,
    borderRadius: 8,
  },
  pricingTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
  },
  pricingText: {
    fontSize: 14,
    color: '#6c757d',
    lineHeight: 20,
    marginBottom: 8,
  },
  pricingExample: {
    fontSize: 14,
    color: '#495057',
    fontWeight: '600',
  },
  buttonsContainer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  skipButton: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#6c757d',
    alignItems: 'center',
  },
  skipButtonText: {
    color: '#6c757d',
    fontSize: 16,
    fontWeight: '600',
  },
  completeButton: {
    flex: 1,
    backgroundColor: '#28a745',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  completeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default BusinessSetupPaymentScreen;

